import { parseMimeType } from "@scalar/helpers/http/mime-type";
//#region src/v2/blocks/response-block/helpers/response-stream-multipart.ts
var MAX_PART_SIZE = 8388608;
var MAX_NESTING = 8;
/** Preserve every byte until MIME boundaries identify a complete part. */
var decodeBytes = (bytes) => Uint8Array.from(bytes, (char) => char.charCodeAt(0));
/** Parse MIME delimiter lines, never treating boundary-like body bytes as separators. */
var createMultipartParser = (contentType, emit, nesting = 0, prefix = "") => {
	const boundary = parseMimeType(contentType).parameters.get("boundary");
	if (!boundary || !/^[0-9A-Za-z'()+_,\-./:=? ]{1,70}$/.test(boundary) || boundary.endsWith(" ")) throw new Error("Multipart response requires a valid boundary parameter.");
	if (nesting >= MAX_NESTING) throw new Error("Multipart nesting exceeds the display limit.");
	const marker = new TextEncoder().encode(`--${boundary}`);
	let buffer = /* @__PURE__ */ new Uint8Array(1024);
	let length = 0;
	let offset = 0;
	let lineOffset = 0;
	let started = false;
	let closed = false;
	let partNumber = 0;
	const emitPart = (part) => {
		if (part.length > MAX_PART_SIZE) throw new Error("Multipart part exceeds the 8 MiB display limit.");
		const separator = part.startsWith("\r\n") ? 0 : part.indexOf("\r\n\r\n");
		if (separator < 0) throw new Error("Multipart part is missing its header separator.");
		const headers = part.slice(0, separator);
		const body = part.slice(separator + (separator === 0 ? 2 : 4));
		const partType = headers.replace(/\r\n[ \t]+/g, " ").match(/^content-type:\s*(.+)$/im)?.[1]?.trim() ?? "text/plain";
		const parsedType = parseMimeType(partType);
		partNumber++;
		const label = `${prefix}${partNumber}`;
		emit(`Part ${label}\n${headers ? `${headers}\n\n` : "\n"}`);
		if (parsedType.type === "multipart") {
			const child = createMultipartParser(partType, emit, nesting + 1, `${label}.`);
			child.push(decodeBytes(body));
			child.finish();
		} else if (parsedType.type === "text" || /(?:json|xml)$/.test(parsedType.subtype)) {
			const text = new TextDecoder(parsedType.parameters.get("charset") ?? "utf-8").decode(decodeBytes(body));
			let formatted = text;
			try {
				formatted = JSON.stringify(JSON.parse(text), null, 2);
			} catch {}
			emit(`${formatted}\n`);
		} else emit(`[Binary part: ${body.length} bytes, base64]\n${btoa(body)}\n`);
	};
	const toByteString = (bytes) => {
		const slices = [];
		for (let index = 0; index < bytes.length; index += 8192) slices.push(String.fromCharCode(...bytes.subarray(index, index + 8192)));
		return slices.join("");
	};
	const drain = (final) => {
		while (!closed && offset + marker.length <= length) {
			const index = offset;
			if (!((index === 0 || buffer[index - 2] === 13 && buffer[index - 1] === 10) && marker.every((byte, position) => buffer[index + position] === byte))) {
				offset++;
				continue;
			}
			const afterMarker = index + marker.length;
			lineOffset = Math.max(lineOffset, afterMarker);
			while (lineOffset + 1 < length && !(buffer[lineOffset] === 13 && buffer[lineOffset + 1] === 10)) lineOffset++;
			const lineEnd = lineOffset + 1 < length ? lineOffset : -1;
			if (lineEnd < 0 && !final) break;
			const suffix = toByteString(buffer.subarray(afterMarker, lineEnd < 0 ? length : lineEnd));
			const closing = /^--[ \t]*$/.test(suffix);
			if (!closing && !/^[ \t]*$/.test(suffix)) {
				offset = afterMarker;
				lineOffset = 0;
				continue;
			}
			if (started) emitPart(toByteString(buffer.subarray(0, Math.max(0, index - 2))));
			started = true;
			const consumed = lineEnd < 0 ? length : lineEnd + 2;
			buffer.copyWithin(0, consumed, length);
			length -= consumed;
			closed = closing;
			offset = 0;
			lineOffset = 0;
		}
		if (length > MAX_PART_SIZE + marker.length + 6) throw new Error("Multipart part exceeds the 8 MiB display limit.");
		if (final && !closed) throw new Error("Multipart response ended before its closing boundary.");
	};
	return {
		push: (chunk) => {
			for (let index = 0; index < chunk.length && !closed; index += 65536) {
				const slice = chunk.subarray(index, index + 65536);
				if (length + slice.length > buffer.length) {
					const grown = new Uint8Array(Math.max(buffer.length * 2, length + slice.length));
					grown.set(buffer.subarray(0, length));
					buffer = grown;
				}
				buffer.set(slice, length);
				length += slice.length;
				drain(false);
			}
		},
		finish: () => drain(true)
	};
};
//#endregion
export { createMultipartParser };

//# sourceMappingURL=response-stream-multipart.js.map